// collections.js
// Which in-game set an item belongs to — "AK-47 | Vulcan" is from The Huntsman
// Collection, "Sticker | Titan (Holo) | Katowice 2014" from EMS Katowice 2014
// Legends — so a folder can be filled from the set the items already share
// instead of a name typed by hand.
//
// "Set" rather than "collection" because there are three tiers of them, and the
// user holds items from all three:
//
//   collection   the 106 named sets: The Huntsman Collection, The Kilowatt
//                Collection. A skin's home, and always preferred when an item
//                is in both a collection and the case that drops it.
//   case         a weapon case, for what no collection claimed
//   capsule      sticker, autograph and patch capsules
//   souvenir / music / graffiti / pins — the rest of the containers
//
// The data comes from public/collections.json, built by
// scripts/build-collections.js out of all.json. See that file for why the
// membership has to be read off the container entries rather than off the items
// themselves, and why knives and gloves resolve to nothing.
//
// It is a separate fetch from skins-lite.json on purpose: 1 MB (127 KB over the
// wire) that nobody pays for until something asks for a set, and it leaves the
// skins file free to stay trimmed to the three fields it has.

import { useEffect, useState } from 'react';

const URL = '/collections.json';

/** What each `kind` is called on screen. */
export const KIND_LABEL = {
  collection: 'collection',
  case:       'case',
  capsule:    'capsule',
  souvenir:   'souvenir package',
  music:      'music kit box',
  graffiti:   'graffiti box',
  pins:       'pin capsule',
  other:      'container',
};

// Kept in sync with normalizeItemName() in scripts/build-collections.js — the
// index's keys were written by that one, so a query spelled differently finds
// nothing. Change one, change both.
const WEAR = / \((Factory New|Minimal Wear|Field-Tested|Well-Worn|Battle-Scarred)\)$/;

/**
 * One item name in the spelling the index uses.
 *
 * A set is a property of the skin, not of the copy you hold: the wear, the
 * StatTrak counter and the Souvenir badge all fall away, because
 * "StatTrak™ AK-47 | Redline (Field-Tested)" and "AK-47 | Redline" came out of
 * the same place. The ★ goes too, though knives resolve to nothing either way.
 */
export function normalizeItemName(name) {
  return String(name || '')
    .replace(WEAR, '')
    .replace(/^★\s*/, '')
    .replace(/^StatTrak™\s*/, '')
    .replace(/^Souvenir\s*/, '')
    .trim()
    .toLowerCase();
}

// One fetch per page load, shared by every component that asks. Kept at module
// level rather than in a context because the file never changes at runtime and
// nothing writes to it.
let cached = null;

function load() {
  if (!cached) {
    // no-cache forces a revalidation rather than a blind reuse. The file is
    // regenerated whenever all.json is replaced, and a browser holding the
    // previous body would silently look up names against an index that no
    // longer matches the app reading it. A 304 costs nothing when it has not
    // changed.
    cached = fetch(URL, { cache: 'no-cache' })
      .then(r => {
        if (!r.ok) throw new Error(`${URL} -> ${r.status}`);
        return r.json();
      })
      .then(d => ({
        // `collections` is the key an earlier build of this file used. Reading
        // both means a stale copy degrades to working rather than to an index
        // whose every lookup resolves to undefined.
        sets: Array.isArray(d?.sets) ? d.sets
            : Array.isArray(d?.collections) ? d.collections
            : [],
        items: d?.items || {},
      }))
      .catch(err => {
        // A missing index costs the set features and nothing else, so this
        // resolves empty rather than rejecting: every caller below reads an
        // empty index as "no set known", which is already the case they handle
        // for knives.
        console.error('Failed to load collections:', err);
        return { sets: [], items: {} };
      });
  }
  return cached;
}

const EMPTY = { sets: [], items: {}, ready: false };

/** The set index, fetched once and shared. `ready` is false until it lands. */
export function useItemSets() {
  const [index, setIndex] = useState(EMPTY);
  useEffect(() => {
    let alive = true;
    load().then(d => { if (alive) setIndex({ ...d, ready: true }); });
    return () => { alive = false; };
  }, []);
  return index;
}

/** The set an item name belongs to, or null. */
export function setOfName(index, name) {
  const key = normalizeItemName(name);
  if (!key) return null;
  const i = index?.items?.[key];
  return i === undefined ? null : (index.sets[i] || null);
}

/** The set a tracker row's skin belongs to, or null. */
export function setOfRow(index, row) {
  return setOfName(index, row?.skin?.value);
}

/**
 * A set by its id — what a folder stores when it stands for one.
 *
 * A scan rather than a map: this is called once per folder drawn, and a user
 * has a handful of folders, not hundreds. Returns null for a folder the user
 * named themselves, and for a set id that no longer exists in a rebuilt index,
 * which the callers draw as an ordinary folder.
 */
export function setById(index, id) {
  if (!id) return null;
  return index?.sets?.find(s => s.id === id) || null;
}

/**
 * The sets a batch of item names covers, biggest share first.
 *
 * Names with no known set are counted separately rather than dropped: "12 of
 * these 20 are from The Huntsman Collection" is a different offer from "all 20
 * are", and the picker says which.
 *
 * @returns {{ groups: {set, count, names}[], unknown: number }}
 */
export function groupNamesBySet(index, names = []) {
  const byId = new Map();
  let unknown = 0;
  for (const name of names) {
    const s = setOfName(index, name);
    if (!s) { unknown++; continue; }
    if (!byId.has(s.id)) byId.set(s.id, { set: s, count: 0, names: [] });
    const g = byId.get(s.id);
    g.count++;
    g.names.push(name);
  }
  const groups = [...byId.values()].sort(
    (a, b) => b.count - a.count || a.set.name.localeCompare(b.set.name),
  );
  return { groups, unknown };
}

/**
 * Plan the bulk "file the unfiled pile by its set" action, and write the text
 * that describes it.
 *
 * Lives here rather than in the two Vault tabs because they would otherwise
 * carry two copies of the same rules and two copies of the same wording, and
 * the wording is the hard part: "nothing to file" has three different causes
 * and a message that does not say which is a message that reads as a bug.
 *
 * @param index      the set index from useItemSets
 * @param positions  the UNFILED positions only, from buildVault
 * @returns {{ groups, units, message }} — groups is empty when there is
 *          nothing to do, and message is then the explanation rather than the
 *          confirmation.
 */
export function planFileBySet(index, positions = []) {
  const byId = new Map();
  const skipped = new Map();   // name -> units that could not be placed

  for (const p of positions) {
    const s = setOfName(index, p.skin);
    if (!s) {
      skipped.set(p.skin, (skipped.get(p.skin) || 0) + p.qty);
      continue;
    }
    if (!byId.has(s.id)) byId.set(s.id, { set: s, indexes: [] });
    byId.get(s.id).indexes.push(...p.units.map(u => u.realIndex));
  }

  const groups = [...byId.values()].sort((a, b) => b.indexes.length - a.indexes.length);
  const units  = groups.reduce((n, g) => n + g.indexes.length, 0);
  const lost   = [...skipped.values()].reduce((n, c) => n + c, 0);
  const plural = (n, w) => `${n} ${w}${n !== 1 ? 's' : ''}`;

  if (!units) {
    // Three different nothings. Saying which one it is turns a dead end into an
    // answer, and the third one names the items so the user can see the call
    // was about their knives rather than about the feature being broken.
    if (!positions.length) {
      return { groups, units, message:
        'Everything in the vault is already in a folder. This only touches unfiled units, so nothing would move.' };
    }
    const named = [...skipped.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([name, n]) => `  ${name}${n > 1 ? ` (${n})` : ''}`)
      .join('\n');
    return { groups, units, message:
      `Nothing to file. The game data cannot place ${plural(lost, 'unfiled unit')}:\n\n${named}\n\n`
      + 'Knives and gloves drop from several cases each, so there is no single set to file them under. '
      + 'Move them by hand, or make a folder of your own for them.' };
  }

  const lines = groups
    .map(g => `  ${g.set.name} (${plural(g.indexes.length, 'unit')})`)
    .join('\n');
  const tail = lost > 0
    ? `\n\n${plural(lost, 'unit')} ${lost === 1 ? 'stays' : 'stay'} unfiled: knives and gloves drop `
      + 'from several cases each, so there is no single set to file them under.'
    : '';

  return { groups, units, message:
    `File ${plural(units, 'unfiled unit')} into ${plural(groups.length, 'folder')}?\n\n${lines}${tail}\n\n`
    + 'Folders you already have are reused, not duplicated.' };
}
